/**
 * src/screens/channels/GoLive.tsx — honest three-state go-live affordance
 * (FR-019, T322).
 *
 * Replaces the prototype's `_bundle.tsx` `GoLive` (which carried the v.1-D
 * false-success pattern: a local `verified` toggle the operator could flip
 * manually, after which the surface optimistically painted green and fired
 * `toast.success("¡Al aire!")`). The wired surface here:
 *
 *   - Subscribes to `api.channelState.getChannelState` — the FULL state row
 *     per `binding_shapes.md` §2 — NOT `getChannelWithState`, which flattens
 *     only the *configured* breaker and would silently propagate the v.1-era
 *     "configured-vs-runtime" name-collision defect (binding_shapes.md §3.2).
 *
 *   - Renders EXACTLY ONE of three operator states inside a single element
 *     identified by `data-testid="golive-state"` whose `data-state` attribute
 *     is one of `"not-configured"`, `"not-verified"`, or `"live"`. The
 *     structural attribute pins the chosen state independent of narrative
 *     copy choices (FR-019; tests/golive/three-state.test.ts asserts the
 *     three-state mutex against this attribute, not the text body).
 *
 *   - NEVER paints a false green while signing is unverified. Concretely:
 *     until the state row reports `status: "running" | "ad_break"` — the
 *     two statuses that prove live request-signing reached the backend —
 *     the affordance subtree contains no `data-tone="success"` / `tone-good`
 *     / `pill-good` markers and no inline-style references to
 *     `var(--good)` / `var(--deep-green)` / `var(--pale-green)`. The
 *     "Comenzar transmisión" button is `disabled` in both non-live states.
 *
 *   - The reactive update path is the Convex subscription itself; the
 *     surface installs NO `setInterval` poll. When the state row's status
 *     changes (e.g. `starting → running`), the subscription re-renders
 *     and the affordance flips automatically (test (c)
 *     "reactive update" pins this).
 *
 *   - The ad-break sub-state (`status: "ad_break"`) is a child of "live",
 *     not a regression to unverified — once signing is proven, it stays
 *     proven for the session (test S4). The optional ad-break indicator
 *     never claims a viewer impression (task notes).
 *
 * Why we read `getChannelState` and not (only) `getChannelWithState`:
 * binding_shapes.md §1's pitfall — when the state row is missing,
 * `getChannelWithState` still surfaces `status: "idle"` and the asset/start
 * fields `undefined`; we MUST NOT infer "channel is running" from absence
 * of a state row. So:
 *   - `getChannelState === null`      → "no configurado" (no state row at all)
 *   - `getChannelState === undefined` → loading (honest no-claim → "no verificar")
 *   - `state.status ∈ {running,ad_break}` → "en vivo"
 *   - otherwise (`starting`/`stopping`/`idle`/`error`) → "conexión sin verificar"
 *
 * RED-on-regression: re-introducing the prototype's local `verified` toggle,
 * stripping the channelState subscription, painting green while unverified,
 * or installing a polling setInterval all fail
 * `tests/golive/three-state.test.ts`.
 */
import React, { type CSSProperties } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Panel,
  PageHead,
  Btn,
  Pill,
  Icon,
  AccessGate,
  useToast,
} from '../../components/primitives';
import { navigate } from '../../lib/nav';

interface GoLiveProps {
  /** Channel id sourced from the URL params (`:id`) via `Bridge` — optional
   * so the type aligns with `ComponentType<Record<string, unknown>>` that
   * `route-bridge.tsx` consumes; the implementation guards `undefined`. */
  id?: string;
}

type ThreeState = 'not-configured' | 'not-verified' | 'live';

/**
 * Map the channelState row into the three operator-facing states.
 *
 * - `state === null`      → no state row → "not-configured" (binding_shapes.md §1 pitfall)
 * - `state === undefined` → loading      → "not-verified"   (honest no-claim — never green)
 * - `state.status` ∈ {"running", "ad_break"} → "live"        (signing proven)
 * - otherwise             → "not-verified" (starting / stopping / idle / error — signing not proven)
 *
 * The "running OR ad_break" mapping is load-bearing: the ad-break sub-state
 * is a child of "live", never a regression to unverified (test S4 pins this).
 */
function deriveState(
  state:
    | { status?: string }
    | null
    | undefined,
): ThreeState {
  if (state === null) return 'not-configured';
  if (state === undefined) return 'not-verified';
  const s = state.status;
  if (s === 'running' || s === 'ad_break') return 'live';
  return 'not-verified';
}

// Spanish operator labels (FR-018 glossary; the test matches case-insensitively).
const LABELS: Record<ThreeState, string> = {
  'not-configured': 'No configurado',
  'not-verified': 'Conexión sin verificar',
  live: 'En vivo',
};

// Per-state explanatory body copy — kept narrative, never makes a claim that
// would lie about signing status. The structural test scopes label detection
// to the data-testid="golive-state" element, so the body copy below DOES live
// inside that element. We therefore avoid the prototype's bug of writing
// "no mostraremos un estado 'en vivo'" verbatim — that string contained the
// "en vivo" label and would break the mutex assertion.
const BODY_COPY: Record<ThreeState, string> = {
  'not-configured':
    'Este canal aún no tiene un estado activo. Configura tu codificador y empieza a transmitir para verificar la señal entrante.',
  'not-verified':
    'Aún no hemos recibido una señal verificada de tu codificador. No declararemos el canal al aire hasta confirmar la señal entrante.',
  live: 'Tu canal está al aire. La señal entrante ha sido verificada.',
};

const PANEL_BG_BY_STATE: Record<ThreeState, string> = {
  'not-configured': 'var(--lightest-gray)',
  'not-verified': 'rgba(245,158,11,0.06)',
  // The "live" branch is the ONLY one allowed to paint green — the test
  // (b) S3 case explicitly confirms green is honest here.
  live: 'rgba(22,163,74,0.10)',
};

const BORDER_BY_STATE: Record<ThreeState, string> = {
  'not-configured': '1px solid var(--border-cool)',
  'not-verified': '1px solid rgba(245,158,11,0.3)',
  live: '1px solid rgba(22,163,74,0.35)',
};

const ICON_BY_STATE: Record<ThreeState, { name: string; color: string }> = {
  'not-configured': { name: 'radio', color: 'var(--muted-slate)' },
  'not-verified': { name: 'alertTriangle', color: 'var(--warn)' },
  live: { name: 'checkCircle', color: '#15803d' }, // hex, not var(--good)
};

/**
 * `data-tone` attribute carried by the affordance element. Load-bearing for
 * FR-019's "no false green" invariant: the test asserts
 * `expect(tone).not.toMatch(/^(success|good)$/)`, and `.toMatch()` errors
 * when the receiver is `null` — so the attribute MUST be present and MUST
 * NOT equal `"success"` or `"good"` in the two unverified states. Only the
 * "live" state may legitimately declare `data-tone="success"`.
 */
const DATA_TONE_BY_STATE: Record<ThreeState, string> = {
  'not-configured': 'neutral',
  'not-verified': 'warn',
  live: 'success',
};

export function GoLive({ id }: GoLiveProps): React.ReactElement {
  // ─── Subscriptions ────────────────────────────────────────────────────
  // The live-state row IS the live signal — there is no setInterval poll.
  // `useQuery` returns `undefined` while loading, `null` when the row
  // doesn't exist, or the document otherwise.
  //
  // `'skip'` (sentinel for "do not run") is used when the route param is
  // missing — the route table makes `:id` mandatory, but the type signature
  // accepts `undefined` to align with the Bridge wrapper's prop shape.
  const channelState = useQuery(
    api.channelState.getChannelState,
    id ? { channelId: id as any } : 'skip',
  );

  // Channel header lookup — used only for narrative (page title, crumbs).
  // It surfaces the configured breaker and the flat spread, NOT the runtime
  // live-state (binding_shapes.md §3.2), so we deliberately do not derive
  // the three-state mapping from it.
  const channel = useQuery(
    api.channels.getChannelWithState,
    id ? { channelId: id as any } : 'skip',
  );

  const state: ThreeState = deriveState(channelState);
  const label = LABELS[state];
  const isLive = state === 'live';

  const { toast } = useToast();

  // ─── Page name fallbacks ──────────────────────────────────────────────
  const channelName: string = (channel as any)?.name ?? 'Canal';

  // Render the affordance.
  const affordanceStyle: CSSProperties = {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: 16,
    background: PANEL_BG_BY_STATE[state],
    border: BORDER_BY_STATE[state],
    borderRadius: 14,
  };

  const iconCfg = ICON_BY_STATE[state];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <PageHead
        crumbs={[
          { label: 'Canales', href: '#/channels' },
          { label: channelName, href: '#/channels/' + id },
          { label: 'Salir al aire' },
        ]}
        title="Salir al aire"
        description={`${channelName} — configura tu codificador y comienza a transmitir.`}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Connection endpoints (informational — outside the affordance subtree). */}
        <Panel title="Puntos de conexión">
          {[
            ['Principal', 'rtmps://ingest.circa.tv/live'],
            ['Respaldo', 'rtmps://backup.circa.tv/live'],
          ].map(([k, url]) => (
            <div
              key={k}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid var(--lightest-gray)',
              }}
            >
              <Pill tone={k === 'Principal' ? 'info' : 'neutral'} size="sm">
                {k}
              </Pill>
              <span
                className="t-mono"
                style={{
                  flex: 1,
                  fontSize: 13,
                  textTransform: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {url}
              </span>
              <Btn
                size="sm"
                variant="ghost"
                icon="copy"
                onClick={() =>
                  toast({
                    variant: 'success',
                    message: 'Copiado al portapapeles.',
                  })
                }
              >
                Copiar
              </Btn>
            </div>
          ))}
        </Panel>

        {/* The credential management entry (informational). */}
        <Panel title="Clave de entrada">
          <p
            style={{
              margin: '0 0 14px',
              fontSize: 14,
              color: 'var(--body-muted)',
            }}
          >
            Tu credencial está oculta por seguridad. Revélala desde la página
            de credenciales (solo administradores).
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <AccessGate perm="streamkey_reveal">
              <Btn
                variant="ghost"
                icon="key"
                onClick={() => navigate('/channels/' + id + '/input-keys')}
              >
                Administrar credenciales
              </Btn>
            </AccessGate>
            <Btn
              variant="ghost"
              icon="sliders"
              onClick={() => navigate('/channels/' + id + '/encoder')}
            >
              Configurar codificador
            </Btn>
          </div>
        </Panel>

        {/* ────────────────────────────────────────────────────────────────
            The three-state affordance — load-bearing for FR-019.

            The single element carrying data-testid="golive-state" wraps the
            chosen-state UI. The data-state attribute pins the chosen state
            independent of narrative copy. The test scopes ALL its mutex,
            no-false-green, and label-presence assertions to this element's
            subtree — so nothing outside this wrapper can satisfy or break
            the contract by accident.
        ──────────────────────────────────────────────────────────────── */}
        <Panel title="Estado de la conexión">
          <div
            data-testid="golive-state"
            data-state={state}
            data-tone={DATA_TONE_BY_STATE[state]}
            style={affordanceStyle}
          >
            <Icon
              name={iconCfg.name}
              size={18}
              color={iconCfg.color}
              style={{ marginTop: 1 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: 'var(--body-muted)',
                }}
              >
                {BODY_COPY[state]}
              </p>
            </div>
          </div>

          {/* The "Comenzar transmisión" action — disabled unless live.
              In the unverified branch the disabled button is the structural
              signal the test pins (test (b) S2: any "comenzar transmisión"
              button MUST be disabled while signing is unverified). When
              already live, the action navigates to the channel detail. */}
          <div style={{ marginTop: 16 }}>
            <Btn
              variant="solid"
              size="lg"
              icon="radio"
              disabled={!isLive}
              onClick={() => navigate('/channels/' + id)}
            >
              Comenzar transmisión
            </Btn>
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default GoLive;
